import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, Instagram, User, LogOut, Package, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import AuthModal from "@/components/AuthModal";

var navLinks = [
  { name: "Home", path: "/" },
  { name: "Shop", path: "/shop" },
  { name: "About", path: "/about" },
  { name: "Partner Program", path: "/partner-program" }
];

export default function Navbar() {
  var [isScrolled, setIsScrolled] = useState(false);
  var [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  var [showAuthModal, setShowAuthModal] = useState(false);
  var [showUserMenu, setShowUserMenu] = useState(false);
  var location = useLocation();
  var navigate = useNavigate();
  var cart = useCart();
  var auth = useAuth();
  var wishlist = useWishlist();
  var cartCount = cart.getCartCount();

  useEffect(function handleScroll() {
    function onScroll() { setIsScrolled(window.scrollY > 20); }
    window.addEventListener("scroll", onScroll);
    return function() { window.removeEventListener("scroll", onScroll); };
  }, []);

  useEffect(function closeMenuOnNavigate() {
    setIsMobileMenuOpen(false);
    setShowUserMenu(false);
  }, [location]);

  function handleLogout() {
    auth.logout();
    setShowUserMenu(false);
  }

  return (
    <>
      <header
        className={"fixed left-0 right-0 z-50 transition-all duration-300 " + (isScrolled ? "glass py-3" : "bg-transparent py-5")}
        style={{ top: "var(--banner-height, 0px)" }}
        data-testid="navbar"
      >
        <div className="container-tactical">
          <nav className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
              <div className="w-10 h-10 bg-[#C0A062] rounded-sm flex items-center justify-center">
                <span className="font-heading font-black text-black text-lg">RGP</span>
              </div>
              <div className="hidden sm:block">
                <h1 className="font-heading font-bold text-white text-lg tracking-tight uppercase leading-none">Resurrected</h1>
                <p className="font-mono text-[10px] text-[#A1A1AA] tracking-widest uppercase">Gun Parts</p>
              </div>
            </Link>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map(function(link) {
                return (
                  <Link key={link.path} to={link.path} className={"nav-link font-heading font-semibold text-sm uppercase tracking-wider " + (location.pathname === link.path ? "active" : "")} data-testid={"nav-link-" + link.name.toLowerCase().replace(" ", "-")}>
                    {link.name}
                  </Link>
                );
              })}
            </div>

            <div className="hidden lg:flex items-center gap-4">
              <a href="https://instagram.com/resurrectedgunparts" target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-[#C0A062] transition-colors" data-testid="instagram-link">
                <Instagram className="w-5 h-5" />
              </a>

              {auth.isAuthenticated && (
                <Link to="/wishlist" className="relative text-[#A1A1AA] hover:text-[#C0A062] transition-colors" data-testid="wishlist-nav-link" aria-label="Wishlist">
                  <Heart className="w-5 h-5" />
                  {wishlist.count > 0 && <span className="absolute -top-2 -right-2 w-4 h-4 bg-[#C0A062] text-black text-[10px] font-bold rounded-full flex items-center justify-center">{wishlist.count}</span>}
                </Link>
              )}

              {auth.isAuthenticated ? (
                <div className="relative">
                  <button onClick={function() { setShowUserMenu(!showUserMenu); }} className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors" data-testid="user-menu-button">
                    {auth.user?.picture ? (
                      <img src={auth.user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#C0A062] flex items-center justify-center">
                        <User className="w-4 h-4 text-black" />
                      </div>
                    )}
                    <span className="font-heading text-sm uppercase">{auth.user?.name?.split(" ")[0]}</span>
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-[#121212] border border-white/10 rounded-sm py-2 z-50">
                      <Link to="/orders" className="flex items-center gap-2 px-4 py-2 text-[#A1A1AA] hover:text-white hover:bg-white/5" data-testid="orders-link">
                        <Package className="w-4 h-4" />Orders
                      </Link>
                      <Link to="/wishlist" className="flex items-center gap-2 px-4 py-2 text-[#A1A1AA] hover:text-white hover:bg-white/5" data-testid="wishlist-menu-link">
                        <Heart className="w-4 h-4" />Wishlist{wishlist.count > 0 ? " (" + wishlist.count + ")" : ""}
                      </Link>
                      <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-[#A1A1AA] hover:text-white hover:bg-white/5 w-full" data-testid="logout-button">
                        <LogOut className="w-4 h-4" />Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Button variant="ghost" size="sm" className="text-[#A1A1AA] hover:text-white" onClick={function() { setShowAuthModal(true); }} data-testid="sign-in-button">
                  <User className="w-4 h-4 mr-2" />Sign In
                </Button>
              )}

              <Button variant="outline" size="sm" className="border-white/10 bg-transparent hover:bg-white/5 text-white font-heading uppercase tracking-wider relative" onClick={function() { cart.setIsCartOpen(true); }} data-testid="cart-button">
                <ShoppingBag className="w-4 h-4 mr-2" /><span>Cart</span>
                {cartCount > 0 && <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#C0A062] text-black text-xs font-bold rounded-full flex items-center justify-center">{cartCount}</span>}
              </Button>
            </div>

            <button className="lg:hidden text-white p-2" onClick={function() { setIsMobileMenuOpen(!isMobileMenuOpen); }} data-testid="mobile-menu-button">
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </nav>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="lg:hidden glass mt-2" data-testid="mobile-menu">
              <div className="container-tactical py-6 space-y-4">
                {navLinks.map(function(link) {
                  return <Link key={link.path} to={link.path} className={"block font-heading font-semibold text-lg uppercase tracking-wider py-2 " + (location.pathname === link.path ? "text-[#C0A062]" : "text-white")}>{link.name}</Link>;
                })}
                <div className="pt-4 flex items-center gap-4">
                  {auth.isAuthenticated ? (
                    <>
                      <Link to="/orders" className="text-[#A1A1AA]"><Package className="w-6 h-6" /></Link>
                      <Link to="/wishlist" className="relative text-[#A1A1AA]"><Heart className="w-6 h-6" />{wishlist.count > 0 && <span className="absolute -top-2 -right-2 w-4 h-4 bg-[#C0A062] text-black text-[10px] font-bold rounded-full flex items-center justify-center">{wishlist.count}</span>}</Link>
                      <button onClick={handleLogout} className="text-[#A1A1AA]"><LogOut className="w-6 h-6" /></button>
                    </>
                  ) : (
                    <button onClick={function() { setShowAuthModal(true); setIsMobileMenuOpen(false); }} className="text-[#A1A1AA]"><User className="w-6 h-6" /></button>
                  )}
                  <a href="https://instagram.com/resurrectedgunparts" target="_blank" rel="noopener noreferrer" className="text-[#A1A1AA] hover:text-[#C0A062]">
                    <Instagram className="w-6 h-6" />
                  </a>
                  <Button variant="outline" className="border-white/10 bg-transparent text-white font-heading uppercase relative" onClick={function() { cart.setIsCartOpen(true); }}>
                    <ShoppingBag className="w-4 h-4 mr-2" />Cart
                    {cartCount > 0 && <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#C0A062] text-black text-xs font-bold rounded-full flex items-center justify-center">{cartCount}</span>}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <AuthModal isOpen={showAuthModal} onClose={function() { setShowAuthModal(false); }} initialMode="login" />
    </>
  );
}
